import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Brain, Briefcase, CheckCircle2, Clock3, Lock, Mail, Phone, Search, Star, Trash2, Upload, Users } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import MailImportConsentModal from "../components/MailImportConsentModal";

const MAX_DIRECT_UPLOAD_FILES = 100;

export default function Candidates() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingEmailReviewRef = useRef(null);
  const consentResolverRef = useRef(null);

  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [importing, setImporting] = useState(false);
  const [uploadingCandidates, setUploadingCandidates] = useState(false);
  const [importProcessStep, setImportProcessStep] = useState(0);
  const [candidateImportStartedAt, setCandidateImportStartedAt] = useState(null);
  const [candidateImportElapsedSeconds, setCandidateImportElapsedSeconds] = useState(0);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [savingMailbox, setSavingMailbox] = useState(false);
  const [showMailboxSettings, setShowMailboxSettings] = useState(false);
  const [showImportPlanDetails, setShowImportPlanDetails] = useState(false);
  const [agentGoal, setAgentGoal] = useState(
    "Import candidate CVs from email and prepare them for recruiter review."
  );
  const [agentReport, setAgentReport] = useState(null);
  const [pendingEmailImports, setPendingEmailImports] = useState([]);
  const [alreadyRejectedEmailImports, setAlreadyRejectedEmailImports] = useState([]);
  const [selectedStagingIds, setSelectedStagingIds] = useState([]);
  const [savingApprovedImports, setSavingApprovedImports] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);
  const [newlyImportedIds, setNewlyImportedIds] = useState([]);
  const [enrichmentProgress, setEnrichmentProgress] = useState(null);
  const [lastImportJobId, setLastImportJobId] = useState(null);
  const [mailboxSettings, setMailboxSettings] = useState({
    email_address: "",
    import_query: "has:attachment filename:pdf",
    mailbox_scope: "inbox",
    mailbox_folder_name: "",
    max_results: 10,
    lookback_days: 14,
  });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidatePageSize] = useState(25);
  const [candidateTotal, setCandidateTotal] = useState(0);
  const [candidateTotalPages, setCandidateTotalPages] = useState(1);
  const [importScope, setImportScope] = useState("general");
  const [selectedImportMethod, setSelectedImportMethod] = useState("upload");
  const [selectedEmailProvider, setSelectedEmailProvider] = useState("outlook");
  const [mailImportConsent, setMailImportConsent] = useState({
    open: false,
    provider: "outlook",
  });
  const [selectedImportJobId, setSelectedImportJobId] = useState("");
  const isFree = user?.plan === "free";
  const canImportCandidates = user?.plan === "basic" || user?.plan === "pro" || user?.plan === "pilot" || user?.plan === "enterprise";
  const candidateImportSteps = [
    { key: "uploading", label: "Uploading" },
    { key: "extracting", label: "Extracting" },
    { key: "deduplicating", label: "Deduplicating" },
    { key: "saving", label: "Saving" },
    { key: "ready", label: "Ready for Scan" },
  ];

  useLongActionNotice(importing, `${selectedEmailProvider === "outlook" ? "Outlook" : "Gmail"} import`, 45000);
  useLongActionNotice(uploadingCandidates, "Candidate upload", 45000);
  useLongActionNotice(savingApprovedImports, "Saving email candidate review", 30000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    const jobIdFromUrl = searchParams.get("job_id");
    const scopeFromUrl = searchParams.get("scope");
    const codeFromUrl = searchParams.get("code");
    const savedEmailProvider = localStorage.getItem("emailImportProvider");
    const savedEmailJobId = localStorage.getItem("emailImportJobId");
    const savedEmailLookbackDays = localStorage.getItem("emailImportLookbackDays");
    const savedEmailMaxResults = localStorage.getItem("emailImportMaxResults");
    const savedEmailQuery = localStorage.getItem("emailImportQuery");
    const savedMailboxScope = localStorage.getItem("emailImportMailboxScope");
    const savedMailboxFolderName = localStorage.getItem("emailImportMailboxFolderName");

    if (scopeFromUrl === "job") {
      setImportScope("job");
    }

    if (codeFromUrl && savedEmailProvider) {
      setImportScope("job");
      setSelectedImportMethod("email");
      handleEmailProviderSelect(savedEmailProvider);

      if (savedEmailJobId) {
        setSelectedImportJobId(savedEmailJobId);
      }

      setMailboxSettings((prev) => ({
        ...prev,
        import_query: savedEmailQuery ?? prev.import_query,
        mailbox_scope: savedMailboxScope || prev.mailbox_scope,
        mailbox_folder_name: savedMailboxFolderName || prev.mailbox_folder_name,
        lookback_days: savedEmailLookbackDays || prev.lookback_days,
        max_results: savedEmailMaxResults || prev.max_results,
      }));
      return;
    }

    if (!jobIdFromUrl) return;

    setImportScope("job");
    setSelectedImportMethod("upload");
    setSelectedImportJobId(jobIdFromUrl);
  }, [searchParams]);

  const fetchCandidates = async (page = candidatePage) => {
    setFetching(true);

    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("page_size", candidatePageSize);

      if (query.trim()) {
        params.set("q", query.trim());
      }

      const res = await apiFetch(`/candidates/?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(data?.detail || "Failed to load candidates.");
        return;
      }

      if (Array.isArray(data)) {
        setCandidates(data || []);
        setCandidateTotal(data.length);
        setCandidateTotalPages(1);
      } else {
        setCandidates(data.items || []);
        setCandidateTotal(data.total || 0);
        setCandidateTotalPages(data.total_pages || 1);
        setCandidatePage(data.page || page);
      }
    } catch (err) {
      toast.error("Failed to load candidates.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchCandidates(candidatePage);
  }, [user, candidatePage]);

  useEffect(() => {
    if (!user) return;
    const timeoutId = window.setTimeout(() => {
      setCandidatePage(1);
      fetchCandidates(1);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => {
    setCandidatePage(1);
  }, [statusFilter]);

  const fetchJobs = async () => {
    try {
      const res = await apiFetch("/jobs");
      const data = await res.json();

      if (!res.ok) return;

      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load jobs", err);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchJobs();
  }, [user]);

  useEffect(() => {
    if (!uploadingCandidates && !importing) {
      setImportProcessStep(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setImportProcessStep((step) => Math.min(step + 1, candidateImportSteps.length - 1));
    }, 1400);

    return () => window.clearInterval(intervalId);
  }, [uploadingCandidates, importing, candidateImportSteps.length]);

  useEffect(() => {
    const importRunning = uploadingCandidates || importing || enrichmentProgress?.active;

    if (!importRunning) {
      setCandidateImportStartedAt(null);
      setCandidateImportElapsedSeconds(0);
      return undefined;
    }

    setCandidateImportStartedAt((startedAt) => startedAt || Date.now());

    return undefined;
  }, [uploadingCandidates, importing, enrichmentProgress?.active]);

  useEffect(() => {
    const importRunning = uploadingCandidates || importing || enrichmentProgress?.active;

    if (!candidateImportStartedAt || !importRunning) {
      return undefined;
    }

    const updateElapsed = () => {
      setCandidateImportElapsedSeconds(Math.max(0, Math.floor((Date.now() - candidateImportStartedAt) / 1000)));
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);

    return () => window.clearInterval(intervalId);
  }, [candidateImportStartedAt, uploadingCandidates, importing, enrichmentProgress?.active]);

  const formatElapsedTime = (seconds) => {
    const safeSeconds = Math.max(0, seconds || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const getDefaultEmailQuery = (provider) => (
    provider === "outlook" ? "" : "has:attachment filename:pdf"
  );

  const getEmailProviderName = (provider) => (
    provider === "outlook" ? "Outlook" : "Gmail"
  );

  const scrollToPendingEmailReview = () => {
    window.setTimeout(() => {
      pendingEmailReviewRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const handleEmailProviderSelect = (provider) => {
    localStorage.setItem("candidateImportEmailProvider", provider);
    setSelectedEmailProvider(provider);
  };

  const requestMailboxConsent = (provider) =>
    new Promise((resolve) => {
      consentResolverRef.current = resolve;
      setMailImportConsent({
        open: true,
        provider,
      });
    });

  const closeMailboxConsent = (accepted) => {
    const resolve = consentResolverRef.current;
    consentResolverRef.current = null;
    setMailImportConsent((prev) => ({
      ...prev,
      open: false,
    }));
    resolve?.(accepted);
  };

  const confirmMailboxConsentIfNeeded = async (provider, settingsData = null) => {
    const consentRequired = settingsData?.mailbox_consent_required;

    if (!consentRequired) {
      return true;
    }

    const accepted = await requestMailboxConsent(provider);
    if (!accepted) {
      return false;
    }

    const consentRes = await apiFetch(`/candidate-import/${provider}/consent`, {
      method: "POST",
    });
    const consentData = await consentRes.json();

    if (!consentRes.ok) {
      toast.error(consentData?.detail || "Could not save mailbox consent.");
      return false;
    }

    setMailboxSettings((prev) => ({
      ...prev,
      mailbox_consent_at: consentData.mailbox_consent_at,
      mailbox_consent_policy_version: consentData.mailbox_consent_policy_version,
      mailbox_consent_required: consentData.mailbox_consent_required,
    }));

    return true;
  };

  const fetchMailboxSettings = async (provider = selectedEmailProvider) => {
    try {
      const res = await apiFetch(`/candidate-import/${provider}/settings`);
      const data = await res.json();

      if (!res.ok) return;

      setMailboxSettings({
        email_address: data.email_address || "",
        import_query: data.import_query || getDefaultEmailQuery(provider),
        mailbox_scope: data.mailbox_scope || "inbox",
        mailbox_folder_name: data.mailbox_folder_name || "",
        max_results: data.max_results || 10,
        lookback_days: data.lookback_days || 14,
        mailbox_consent_at: data.mailbox_consent_at,
        mailbox_consent_policy_version: data.mailbox_consent_policy_version,
        mailbox_consent_required: data.mailbox_consent_required,
      });
    } catch (err) {
      console.error(`Failed to load ${provider} settings`, err);
    }
  };

  useEffect(() => {
    if (!user || isFree) return;
    fetchMailboxSettings();
  }, [user, isFree, selectedEmailProvider]);

  const fetchPendingEmailImports = async (jobId = selectedImportJobId) => {
    if (!jobId) {
      setPendingEmailImports([]);
      setSelectedStagingIds([]);
      return;
    }

    try {
      const res = await apiFetch(`/candidates/import-staging?job_id=${jobId}`);
      const data = await res.json();

      if (!res.ok) return;

      setPendingEmailImports(data || []);
      setSelectedStagingIds((data || []).map((item) => item.staging_id).filter(Boolean));
    } catch (err) {
      console.error("Failed to load pending email imports", err);
    }
  };

  useEffect(() => {
    if (!user || importScope !== "job" || !selectedImportJobId) return;
    fetchPendingEmailImports(selectedImportJobId);
  }, [user, importScope, selectedImportJobId]);

  useEffect(() => {
    if (!user) return;
    if (isFree) return;

    const code = searchParams.get("code");
    const senderProvider = localStorage.getItem("candidateEmailConnectProvider");
    const savedProvider = localStorage.getItem("emailImportProvider");

    if (!code || !savedProvider) {
      if (!code || !senderProvider) return;

      const completeSenderConnection = async () => {
        try {
          const res = await apiFetch(`/candidate-email-settings/connect/${senderProvider}/complete`, {
            method: "POST",
            body: JSON.stringify({ code }),
          });
          const data = await res.json();

          localStorage.removeItem("candidateEmailConnectProvider");

          if (!res.ok) {
            toast.error(actionFailureMessage("Candidate email sender connection", data?.detail));
          } else {
            toast.success(`${getEmailProviderName(senderProvider)} sender connected.`);
          }
        } catch {
          toast.error(actionFailureMessage("Candidate email sender connection"));
        } finally {
          navigate("/profile?section=app_configuration", { replace: true });
        }
      };

      completeSenderConnection();
      return;
    }

    const importFromEmail = async () => {
      setImporting(true);
      setImportProcessStep(0);
      setCandidateImportStartedAt(Date.now());
      setCandidateImportElapsedSeconds(0);
      const savedAgentGoal =
        localStorage.getItem("emailImportAgentGoal") ||
        "Import candidate CVs from email and prepare them for recruiter review.";
      const savedJobId = localStorage.getItem("emailImportJobId");
      const savedLookbackDays = localStorage.getItem("emailImportLookbackDays");
      const savedReturnPath = localStorage.getItem("emailImportReturnPath");
      const savedMaxResults = localStorage.getItem("emailImportMaxResults");
      const savedImportQuery = localStorage.getItem("emailImportQuery");
      const savedMailboxScope = localStorage.getItem("emailImportMailboxScope");
      const savedMailboxFolderName = localStorage.getItem("emailImportMailboxFolderName");

      setImportScope("job");
      setSelectedImportMethod("email");
      handleEmailProviderSelect(savedProvider);

      if (savedJobId) {
        setSelectedImportJobId(savedJobId);
      }

      try {
        const res = await apiFetch(`/candidate-import/${savedProvider}/agent-import`, {
          method: "POST",
          body: JSON.stringify({
            code,
            goal: savedAgentGoal,
            job_id: savedJobId ? Number(savedJobId) : null,
            query: savedImportQuery || undefined,
            mailbox_scope: savedMailboxScope || undefined,
            mailbox_folder_name: savedMailboxFolderName || undefined,
            lookback_days: savedLookbackDays ? Number(savedLookbackDays) : undefined,
            max_results: savedMaxResults ? Number(savedMaxResults) : undefined,
          }),
        });
        const data = await res.json();

        if (!res.ok) {
          toast.error(actionFailureMessage(`${getEmailProviderName(savedProvider)} import`, data?.detail));
          return;
        }

        const stagedImports = data.tool_result?.staged || [];
        const rejectedImports = data.tool_result?.already_rejected || [];
        const stagedIds = stagedImports
          .map((item) => item.staging_id)
          .filter(Boolean);

        setPendingEmailImports(stagedImports);
        setAlreadyRejectedEmailImports(rejectedImports);
        setSelectedStagingIds(stagedIds);
        setNewlyImportedIds([]);
        setAgentReport(data);
        setLastImportJobId(savedJobId);
        if (stagedImports.length > 0 || rejectedImports.length > 0) {
          scrollToPendingEmailReview();
        }
        toast.success(`Found ${data.summary?.staged_count || 0} ${getEmailProviderName(savedProvider)} candidate CVs for review.`);
        localStorage.removeItem("emailImportAgentGoal");
        localStorage.removeItem("emailImportJobId");
        localStorage.removeItem("emailImportLookbackDays");
        localStorage.removeItem("emailImportMaxResults");
        localStorage.removeItem("emailImportQuery");
        localStorage.removeItem("emailImportMailboxScope");
        localStorage.removeItem("emailImportMailboxFolderName");
        localStorage.removeItem("emailImportScope");
        localStorage.removeItem("emailImportProvider");
        localStorage.removeItem("emailImportReturnPath");
        if (savedReturnPath === "inbox-to-shortlist" && savedJobId) {
          navigate(`/inbox-to-shortlist?job_id=${savedJobId}&imported=1`, { replace: true });
          return;
        }
        window.history.replaceState({}, "", "/candidates");
      } catch (err) {
        toast.error(actionFailureMessage(`${getEmailProviderName(savedProvider)} import`));
      } finally {
        setImporting(false);
      }
    };

    importFromEmail();
  }, [user, isFree, searchParams]);

  const handleMailboxChange = (event) => {
    const { name, value } = event.target;

    setMailboxSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveMailboxSettings = async () => {
    setSavingMailbox(true);

    try {
      const res = await apiFetch(`/candidate-import/${selectedEmailProvider}/settings`, {
        method: "PUT",
        body: JSON.stringify({
          email_address: mailboxSettings.email_address,
          import_query: mailboxSettings.import_query,
          mailbox_scope: mailboxSettings.mailbox_scope || "inbox",
          mailbox_folder_name: mailboxSettings.mailbox_folder_name || "",
          max_results: Number(mailboxSettings.max_results) || 10,
          lookback_days: Number(mailboxSettings.lookback_days) || 14,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage(`Save ${getEmailProviderName(selectedEmailProvider)} settings`, data?.detail));
        return;
      }

      setMailboxSettings({
        email_address: data.email_address || "",
        import_query: data.import_query || getDefaultEmailQuery(selectedEmailProvider),
        mailbox_scope: data.mailbox_scope || "inbox",
        mailbox_folder_name: data.mailbox_folder_name || "",
        max_results: data.max_results || 10,
        lookback_days: data.lookback_days || 14,
      });
      toast.success(`${getEmailProviderName(selectedEmailProvider)} import settings saved.`);
    } catch (err) {
      toast.error(actionFailureMessage(`Save ${getEmailProviderName(selectedEmailProvider)} settings`));
    } finally {
      setSavingMailbox(false);
    }
  };

  const handleConnectEmail = async () => {
    if (!canImportCandidates) {
      toast.error("Upgrade to Basic or Pro to import candidates.");
      return;
    }

    if (!agentGoal.trim()) {
      toast.error(`Enter an agent goal before running the ${getEmailProviderName(selectedEmailProvider)} agent.`);
      return;
    }

    if (importScope !== "job" || !selectedImportJobId) {
      toast.error("Select a job before importing applicants from email.");
      return;
    }

    if (mailboxSettings.mailbox_scope === "folder" && !mailboxSettings.mailbox_folder_name?.trim()) {
      toast.error("Enter the folder or label name before importing from a specific mail folder.");
      return;
    }

    try {
      const settingsRes = await apiFetch(`/candidate-import/${selectedEmailProvider}/settings`, {
        method: "PUT",
        body: JSON.stringify({
          email_address: mailboxSettings.email_address,
          import_query: mailboxSettings.import_query,
          mailbox_scope: mailboxSettings.mailbox_scope || "inbox",
          mailbox_folder_name: mailboxSettings.mailbox_folder_name || "",
          max_results: Number(mailboxSettings.max_results) || 10,
          lookback_days: Number(mailboxSettings.lookback_days) || 14,
        }),
      });
      const settingsData = await settingsRes.json();

      if (!settingsRes.ok) {
        toast.error(actionFailureMessage(`Save ${getEmailProviderName(selectedEmailProvider)} settings`, settingsData?.detail));
        return;
      }

      const consentAccepted = await confirmMailboxConsentIfNeeded(selectedEmailProvider, settingsData);
      if (!consentAccepted) return;

      setPendingEmailImports([]);
      setAlreadyRejectedEmailImports([]);
      setSelectedStagingIds([]);

      localStorage.removeItem("candidateEmailConnectProvider");
      localStorage.setItem("emailImportAgentGoal", agentGoal);
      localStorage.setItem("emailImportScope", importScope);
      localStorage.setItem("emailImportJobId", selectedImportJobId);
      localStorage.setItem("emailImportProvider", selectedEmailProvider);
      localStorage.setItem("emailImportLookbackDays", String(Number(mailboxSettings.lookback_days) || 14));
      localStorage.setItem("emailImportMaxResults", String(Number(mailboxSettings.max_results) || 10));
      localStorage.setItem("emailImportQuery", mailboxSettings.import_query || "");
      localStorage.setItem("emailImportMailboxScope", mailboxSettings.mailbox_scope || "inbox");
      localStorage.setItem("emailImportMailboxFolderName", mailboxSettings.mailbox_folder_name || "");

      const res = await apiFetch(`/candidate-import/${selectedEmailProvider}/connect`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage(`${getEmailProviderName(selectedEmailProvider)} mailbox connection`, data?.detail));
        return;
      }

      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(actionFailureMessage(`${getEmailProviderName(selectedEmailProvider)} mailbox connection`));
    }
  };

  const handleUploadFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length > MAX_DIRECT_UPLOAD_FILES) {
      toast.error(`You can upload up to ${MAX_DIRECT_UPLOAD_FILES} PDF CVs at once. Please split larger uploads into smaller batches.`);
      setUploadFiles([]);
      event.target.value = "";
      return;
    }

    setUploadFiles(selectedFiles);
  };

  const handleUploadFileClick = (event) => {
    event.stopPropagation();

    if (importScope === "job" && !selectedImportJobId) {
      event.preventDefault();
      toast.error("Select a job before choosing CV files for job applicants.");
    }
  };

  const toggleStagedImportSelection = (stagingId) => {
    setSelectedStagingIds((prev) => (
      prev.includes(stagingId)
        ? prev.filter((id) => id !== stagingId)
        : [...prev, stagingId]
    ));
  };

  const handleApproveStagedImports = async () => {
    if (selectedStagingIds.length === 0) {
      toast.error("Select at least one email candidate to approve.");
      return;
    }

    setSavingApprovedImports(true);

    try {
      const res = await apiFetch("/candidates/import-staging/approve", {
        method: "POST",
        body: JSON.stringify({ staging_ids: selectedStagingIds }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Approve email candidates", data?.detail));
        return;
      }

      const importedIds = (data.approved || [])
        .map((item) => item.candidate_id)
        .filter(Boolean);

      setPendingEmailImports((prev) => prev.filter((item) => !selectedStagingIds.includes(item.staging_id)));
      setSelectedStagingIds([]);
      setNewlyImportedIds(importedIds);
      if (importedIds.length) {
        setEnrichmentProgress({
          active: false,
          searchIndexActive: false,
          candidateIds: importedIds,
          total: importedIds.length,
          ready: importedIds.length,
          failed: 0,
          searchIndexReady: 0,
          searchIndexFailed: 0,
        });
      }
      toast.success(`Approved ${data.approved_count || 0} email candidates.`);
      await fetchCandidates();
    } catch (err) {
      toast.error(actionFailureMessage("Approve email candidates"));
    } finally {
      setSavingApprovedImports(false);
    }
  };

  const handleRejectStagedImports = async () => {
    if (selectedStagingIds.length === 0) {
      toast.error("Select at least one email candidate to reject.");
      return;
    }

    setSavingApprovedImports(true);

    try {
      const res = await apiFetch("/candidates/import-staging/reject", {
        method: "POST",
        body: JSON.stringify({ staging_ids: selectedStagingIds }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Reject email candidates", data?.detail));
        return;
      }

      const rejectedItems = pendingEmailImports
        .filter((item) => selectedStagingIds.includes(item.staging_id))
        .map((item) => ({
          ...item,
          reason: "This CV was already rejected for this job, so it was kept out of the approval queue.",
        }));

      setPendingEmailImports((prev) => prev.filter((item) => !selectedStagingIds.includes(item.staging_id)));
      setAlreadyRejectedEmailImports((prev) => [...rejectedItems, ...prev]);
      setSelectedStagingIds([]);
      toast.success(`Rejected ${data.rejected_count || 0} email candidates.`);
    } catch (err) {
      toast.error(actionFailureMessage("Reject email candidates"));
    } finally {
      setSavingApprovedImports(false);
    }
  };

  const handleDirectUploadImport = async () => {
    if (!canImportCandidates) {
      toast.error("Upgrade to Basic or Pro to import candidates.");
      return;
    }

    if (importScope === "job" && !selectedImportJobId) {
      toast.error("Select a job before importing job applicants.");
      return;
    }

    if (uploadFiles.length === 0) {
      toast.error("Choose at least one PDF CV to import.");
      return;
    }

    if (uploadFiles.length > MAX_DIRECT_UPLOAD_FILES) {
      toast.error(`You can upload up to ${MAX_DIRECT_UPLOAD_FILES} PDF CVs at once. Please split larger uploads into smaller batches.`);
      return;
    }

    const formData = new FormData();
    uploadFiles.forEach((file) => formData.append("cv_files", file));

    if (importScope === "job") {
      formData.append("job_id", selectedImportJobId);
    }

    setUploadingCandidates(true);
    setImportProcessStep(0);
    setCandidateImportStartedAt(Date.now());
    setCandidateImportElapsedSeconds(0);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/candidates/import-upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Candidate upload", data?.detail));
        return;
      }

      setUploadFiles([]);
      setUploadReport(data);
      const importedIds = (data.imported || [])
        .map((item) => item.candidate_id)
        .filter(Boolean);

      setNewlyImportedIds(importedIds);
      if (importedIds.length) {
        setEnrichmentProgress({
          active: false,
          searchIndexActive: false,
          candidateIds: importedIds,
          total: importedIds.length,
          ready: importedIds.length,
          failed: 0,
          searchIndexReady: 0,
          searchIndexFailed: 0,
        });
      }
      setLastImportJobId(importScope === "job" ? selectedImportJobId : null);
      toast.success(`Upload complete. Preparing ${data.imported_count || 0} candidate CVs for AI matching.`);
      await fetchCandidates();
    } catch (err) {
      toast.error(actionFailureMessage("Candidate upload"));
    } finally {
      setUploadingCandidates(false);
    }
  };

  const handleDeleteImportedCandidate = async (candidate) => {
    const confirmed = window.confirm(
      `Delete imported candidate "${candidate.name}" from the candidate library?`
    );

    if (!confirmed) return;

    try {
      const res = await apiFetch(`/candidates/${candidate.candidate_id}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Delete imported candidate", data?.detail));
        return;
      }

      toast.success("Imported candidate deleted.");
      setNewlyImportedIds((prev) =>
        prev.filter((candidateId) => candidateId !== candidate.candidate_id)
      );
      setCandidates((prev) =>
        prev.filter((item) => item.candidate_id !== candidate.candidate_id)
      );
    } catch (err) {
      toast.error(actionFailureMessage("Delete imported candidate"));
    }
  };

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return candidates.filter((candidate) => {
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "shortlisted" && candidate.is_shortlisted) ||
        (statusFilter === "review" && !candidate.is_shortlisted);

      if (!matchesStatus) return false;

      if (!normalizedQuery) return true;

      const searchableText = [
        candidate.name,
        candidate.latest_filename,
        candidate.top_qualification,
        candidate.email,
        candidate.phone,
        candidate.latest_job_name,
        ...(candidate.skills || []),
        ...(candidate.missing_skills || []),
        ...(candidate.jobs || []).map((job) => job.job_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [candidates, query, statusFilter]);

  const displayedCandidates = useMemo(
    () => filteredCandidates.slice(0, candidatePageSize),
    [filteredCandidates, candidatePageSize]
  );

  const stats = useMemo(() => {
    const total = candidateTotal || candidates.length;
    const shortlisted = candidates.filter((candidate) => candidate.is_shortlisted).length;
    const withContact = candidates.filter((candidate) => candidate.email || candidate.phone).length;
    const averageBestScore = candidates.length
      ? Math.round(candidates.reduce((sum, candidate) => sum + (candidate.best_score || 0), 0) / candidates.length)
      : 0;

    return {
      total,
      shortlisted,
      withContact,
      averageBestScore,
    };
  }, [candidates, candidateTotal]);

  const newlyImportedCandidates = useMemo(() => {
    if (!newlyImportedIds.length) return [];

    const newlyImportedSet = new Set(newlyImportedIds);
    return candidates.filter((candidate) => newlyImportedSet.has(candidate.candidate_id));
  }, [candidates, newlyImportedIds]);

  const isNewlyImported = (candidate) => newlyImportedIds.includes(candidate.candidate_id);

  const getEnrichmentStatus = (candidate) => candidate.enrichment_status || "ready";
  const getSearchIndexStatus = (candidate) => candidate.search_index_status || "pending";
  const isCandidateReady = (candidate) => candidate.is_ready_for_scan !== false && getEnrichmentStatus(candidate) === "ready";
  const isCandidateSearchIndexed = (candidate) => getSearchIndexStatus(candidate) === "ready";
  const isImportStillPreparing = Boolean(uploadingCandidates || importing || enrichmentProgress?.active);
  const isImportScanReady = Boolean(enrichmentProgress && !enrichmentProgress.active && enrichmentProgress.total > 0);
  const getApplicationQualityClass = (quality) => {
    switch (quality?.status) {
      case "needs_review":
        return "border-rose-200 bg-rose-50 text-rose-700";
      case "check_details":
        return "border-amber-200 bg-amber-50 text-amber-800";
      default:
        return "border-emerald-200 bg-emerald-50 text-emerald-800";
    }
  };

  const refreshEnrichmentProgress = async () => {
    if (!enrichmentProgress?.candidateIds?.length) return;

    try {
      const ids = enrichmentProgress.candidateIds.join(",");
      const res = await apiFetch(`/candidates/enrichment-status?ids=${encodeURIComponent(ids)}`);
      const data = await res.json();

      if (!res.ok) return;

      const statusById = new Map(
        (data.items || []).map((item) => [item.candidate_id, item])
      );

      setCandidates((prev) => prev.map((candidate) => {
        const status = statusById.get(candidate.candidate_id);

        if (!status) return candidate;

        return {
          ...candidate,
          enrichment_status: status.status,
          enrichment_error: status.error,
          enriched_at: status.enriched_at,
          search_index_status: status.search_index_status,
          search_index_error: status.search_index_error,
          search_indexed_at: status.search_indexed_at,
          is_ready_for_scan: status.status === "ready",
        };
      }));

      setEnrichmentProgress((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          total: data.total || prev.total,
          ready: data.ready || 0,
          failed: data.failed || 0,
          active: !data.complete,
          searchIndexReady: data.search_index_ready || 0,
          searchIndexFailed: data.search_index_failed || 0,
          searchIndexActive: false,
        };
      });

      if (data.complete && enrichmentProgress.active) {
        toast.success(`${data.ready || 0} candidate CV${data.ready === 1 ? "" : "s"} ready for scanning.`);
        fetchCandidates();
      }
    } catch (err) {
      console.error("Failed to refresh candidate enrichment status", err);
    }
  };

  useEffect(() => {
    if (!enrichmentProgress?.active) return;

    refreshEnrichmentProgress();

    const intervalId = window.setInterval(() => {
      refreshEnrichmentProgress();
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [enrichmentProgress?.active, enrichmentProgress?.candidateIds?.join(",")]);

  const buildBulkScanUrl = (jobId, candidateIds = []) => {
    const params = new URLSearchParams();
    params.set("job_id", jobId);

    if (candidateIds.length) {
      params.set("candidate_ids", candidateIds.join(","));
      params.set("source", "newly_imported");
    }

    return `/bulk-scan?${params.toString()}`;
  };

  const leftPanel = <RecruiterSidebar activeItem="candidates" user={user} />;

  const scoreClass = (score) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-rose-200 bg-rose-50 text-rose-700";
  };

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatEmailImportDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getImportStageState = () => {
    if (uploadingCandidates || importing) {
      const activeStep = importing
        ? Math.min(importProcessStep, candidateImportSteps.length - 2)
        : importProcessStep;

      return {
        activeKey: candidateImportSteps[activeStep]?.key || "uploading",
        progress: Math.min(85, Math.round(((activeStep + 1) / candidateImportSteps.length) * 85)),
        title: uploadingCandidates
          ? "Adding candidates to the job"
          : `Running ${getEmailProviderName(selectedEmailProvider)} import agent`,
        subtitle: uploadingCandidates
          ? "Uploading CVs, extracting readable text, checking duplicates, and saving candidates."
          : `${getEmailProviderName(selectedEmailProvider)} is checking recent Inbox emails, validating PDF CVs, and staging matching applicants for recruiter approval.`,
      };
    }

    if (pendingEmailImports.length > 0) {
      return {
        activeKey: "ready",
        progress: 90,
        variant: "review",
        title: "Review staged CVs before saving",
        subtitle: `${pendingEmailImports.length} email candidate${pendingEmailImports.length === 1 ? "" : "s"} matched this job. Approve the right CVs to add them to the candidate library.`,
      };
    }

    if (enrichmentProgress?.active) {
      const completed = enrichmentProgress.ready + enrichmentProgress.failed;
      const progress = enrichmentProgress.total
        ? Math.max(85, Math.round((completed / enrichmentProgress.total) * 100))
        : 85;

      return {
        activeKey: "ready",
        progress,
        title: "Preparing imported candidates for scan",
        subtitle: `${enrichmentProgress.ready} / ${enrichmentProgress.total} candidates ready for scanning.`,
      };
    }

    if (enrichmentProgress && enrichmentProgress.total > 0) {
      return {
        activeKey: "complete",
        progress: 100,
        title: "Candidates are ready for scan",
        subtitle: `${enrichmentProgress.ready} candidates are ready for scanning. AI Talent Search indexing continues in Background Activity.`,
      };
    }

    return null;
  };

  const renderCandidateImportProgress = () => {
    const stageState = getImportStageState();
    if (!stageState) return null;

    const stages = [
      ...candidateImportSteps,
      { key: "complete", label: "Complete" },
    ];
    const activeIndex = stages.findIndex((stage) => stage.key === stageState.activeKey);
    const isReviewStage = stageState.variant === "review";
    const panelClass = isReviewStage
      ? "overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-br from-amber-950 via-emerald-950 to-slate-950 p-5 text-white shadow-lg"
      : "overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-950 p-5 text-white shadow-lg";
    const iconClass = isReviewStage
      ? "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-amber-200/40 bg-amber-300/20"
      : "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-300/30 bg-emerald-400/15";
    const progressClass = isReviewStage
      ? "h-full rounded-full bg-gradient-to-r from-amber-300 via-lime-200 to-emerald-200 shadow-[0_0_18px_rgba(251,191,36,0.55)] transition-all duration-700 ease-out"
      : "h-full rounded-full bg-gradient-to-r from-emerald-300 via-lime-200 to-emerald-100 shadow-[0_0_18px_rgba(110,231,183,0.55)] transition-all duration-700 ease-out";
    const percentClass = isReviewStage
      ? "w-fit rounded-full border border-amber-200/50 bg-amber-100 px-4 py-2 text-sm font-bold text-amber-950 shadow-sm"
      : "w-fit rounded-full border border-emerald-300/40 bg-white px-4 py-2 text-sm font-bold text-emerald-900 shadow-sm";

    return (
      <div className={panelClass}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={iconClass}>
              {isReviewStage ? (
                <CheckCircle2 className="animate-pulse text-amber-200" size={24} />
              ) : (
                <Brain className="animate-pulse text-emerald-200" size={24} />
              )}
            </div>
            <div>
              <p className="text-base font-semibold">{stageState.title}</p>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-50/80">{stageState.subtitle}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-emerald-50">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  {importing ? "Email import" : `${uploadFiles.length || enrichmentProgress?.total || 0} selected`}
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  {importScope === "job" ? "Job applicants" : "General applicants"}
                </span>
                {(uploadingCandidates || importing || enrichmentProgress?.active) && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1">
                    <Clock3 size={13} className="text-emerald-200" />
                    Running {formatElapsedTime(candidateImportElapsedSeconds)}
                  </span>
                )}
                {enrichmentProgress?.total > 0 && (
                  <span className={`rounded-full border border-white/15 bg-white/10 px-3 py-1 ${
                    enrichmentProgress.active ? "animate-pulse" : ""
                  }`}>
                    {enrichmentProgress.ready} / {enrichmentProgress.total} ready
                  </span>
                )}
              </div>
            </div>
          </div>

          <span className={percentClass}>
            {stageState.progress}%
          </span>
        </div>

        <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/15">
          <div
            className={progressClass}
            style={{ width: `${stageState.progress}%` }}
          />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-6">
          {stages.map((stage, index) => {
            const complete = index < activeIndex || stageState.activeKey === "complete";
            const active = index === activeIndex && stageState.activeKey !== "complete";

            return (
              <div
                key={stage.key}
                className={`flex min-h-[52px] items-center justify-center rounded-lg border px-3 text-center text-xs font-semibold transition ${
                  complete
                    ? "border-emerald-300/40 bg-emerald-300/15 text-emerald-50"
                    : active
                    ? isReviewStage
                      ? "animate-pulse border-amber-200 bg-amber-200 text-amber-950 shadow-[0_0_18px_rgba(251,191,36,0.45)]"
                      : "animate-pulse border-lime-200 bg-lime-200 text-emerald-950 shadow-[0_0_18px_rgba(190,242,100,0.35)]"
                    : "border-white/10 bg-white/5 text-emerald-50/45"
                }`}
              >
                <span>{stage.label}</span>
              </div>
            );
          })}
        </div>

        {isReviewStage && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-100 p-4 text-amber-950 shadow-[0_0_24px_rgba(251,191,36,0.22)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-base font-bold">Recruiter approval is needed.</p>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-amber-900">
                  These CVs are only staged. Review the list below, approve the correct applicants, then RecruitGlance can save them for scanning.
                </p>
              </div>
              <button
                type="button"
                onClick={() => pendingEmailReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg bg-amber-950 px-4 text-sm font-semibold text-white transition hover:bg-amber-900"
              >
                Review staged CVs
              </button>
            </div>
          </div>
        )}

        {isImportScanReady && (
          <div className="mt-5 rounded-xl border border-lime-200 bg-lime-100 p-4 text-emerald-950 shadow-[0_0_24px_rgba(190,242,100,0.22)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-base font-bold">Candidates are ready to scan now.</p>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-900">
                  AI Talent Search indexing is continuing in the background. You can scan these candidates immediately,
                  and follow search-index progress from Background Activity.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/candidate-processes")}
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg bg-emerald-950 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900"
              >
                Open Background Activity
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const mainContent = (
    <>
      <MailImportConsentModal
        open={mailImportConsent.open}
        provider={mailImportConsent.provider}
        onCancel={() => closeMailboxConsent(false)}
        onConfirm={() => closeMailboxConsent(true)}
      />

      <section className="overflow-hidden rounded-lg border border-emerald-900/20 bg-[#031b14] shadow-sm">
        <div className="p-5 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
            <Users size={14} />
            Candidate intake
          </div>
          <h2 className="mt-4 text-xl font-semibold">Import Candidates</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-50/80">
            Choose the source, connect candidates to the right job, and prepare them for scanning.
          </p>
        </div>

        <div className="flex flex-col gap-5 border-t border-white/10 bg-white p-5">
          <div>
            <p className="text-sm font-semibold text-slate-950">Import route</p>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Choose whether these candidates are general talent-pool applicants or applicants for a specific job before importing.
            </p>
          </div>

          {!canImportCandidates && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                    <Lock size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-950">
                      Candidate import is a paid feature
                    </p>
                    <p className="mt-1 text-sm leading-6 text-amber-800">
                      You can preview the candidate database workflow here. Upgrade to Basic or Pro to import CVs,
                      build a reusable candidate pool, and scan applicants against jobs.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/profile?section=upgrade")}
                  className="inline-flex min-h-[38px] shrink-0 items-center justify-center rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900"
                >
                  View Plans
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            <button
              type="button"
              onClick={() => setImportScope("general")}
              className={`rounded-lg border p-4 text-left transition ${
                importScope === "general"
                  ? "border-emerald-800 bg-emerald-800 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <Users size={18} />
                <p className="font-semibold">General applicants</p>
              </div>
              <p className={`mt-2 text-sm leading-6 ${importScope === "general" ? "text-slate-200" : "text-slate-500"}`}>
                Import candidates into the reusable talent pool. They can be selected later for any job.
              </p>
            </button>

            <button
              type="button"
              onClick={() => setImportScope("job")}
              className={`rounded-lg border p-4 text-left transition ${
                importScope === "job"
                  ? "border-emerald-800 bg-emerald-800 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <Briefcase size={18} />
                <p className="font-semibold">Applicants for a job</p>
              </div>
              <p className={`mt-2 text-sm leading-6 ${importScope === "job" ? "text-slate-200" : "text-slate-500"}`}>
                Link imported candidates to a selected job so the scan page knows who applied for that role.
              </p>
            </button>
          </div>

          {importScope === "job" && (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Select job</span>
              <select
                value={selectedImportJobId}
                onChange={(event) => setSelectedImportJobId(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                disabled={!canImportCandidates}
              >
                <option value="">Choose a job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            {importScope === "job" && (
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedImportMethod("upload")}
                  disabled={!canImportCandidates}
                  className={`inline-flex min-h-[38px] items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    selectedImportMethod === "upload"
                      ? "border-emerald-700 bg-emerald-800 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:text-emerald-800"
                  }`}
                >
                  <Upload size={16} />
                  Direct Upload
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedImportMethod("email")}
                  disabled={!canImportCandidates || importing || !selectedImportJobId}
                  className={`inline-flex min-h-[38px] items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    selectedImportMethod === "email"
                      ? "border-emerald-700 bg-emerald-800 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:text-emerald-800"
                  }`}
                >
                  <Mail size={16} />
                  Email Agent Import
                </button>
              </div>
            )}

            {selectedImportMethod === "upload" && (
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Upload size={18} className="text-emerald-700" />
                      <p className="font-semibold text-slate-950">Direct Upload</p>
                    </div>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      {importScope === "job"
                        ? "Upload PDF CVs and link them to the selected job."
                        : "Upload PDF CVs into the reusable general applicant pool."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDirectUploadImport}
                    disabled={!canImportCandidates || uploadingCandidates || uploadFiles.length > MAX_DIRECT_UPLOAD_FILES || (importScope === "job" && !selectedImportJobId)}
                    className="inline-flex min-h-[36px] items-center justify-center rounded-lg bg-emerald-800 px-4 text-xs font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {uploadingCandidates
                      ? "Importing..."
                      : uploadFiles.length
                      ? `Import ${uploadFiles.length} file${uploadFiles.length === 1 ? "" : "s"}`
                      : "Import PDFs"}
                  </button>
                </div>
                <input
                  type="file"
                  multiple
                  accept=".pdf,application/pdf"
                  onClick={handleUploadFileClick}
                  onChange={handleUploadFileChange}
                  disabled={!canImportCandidates}
                  className="mt-4 block w-full text-xs text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700"
                />
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Upload up to {MAX_DIRECT_UPLOAD_FILES} PDF CVs at once. Larger candidate sets should be split into separate batches.
                </p>
              </div>
            )}
          </div>

          {importScope === "job" && selectedImportJobId && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Imported candidates will be linked to this job and appear under Applicants for this job in Scan Applicants.
            </div>
          )}

          {importScope === "job" && !selectedImportJobId && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Select a job first to activate Direct Upload or Email Import for job applicants.
            </div>
          )}

          {(uploadingCandidates || importing || pendingEmailImports.length > 0 || enrichmentProgress?.active || isImportScanReady) && (
            renderCandidateImportProgress()
          )}

        </div>
      </section>

      {importScope === "job" && selectedImportMethod === "email" && (
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5">
          <div>
            <div className="flex items-center gap-2">
              <Brain size={20} className="text-slate-500" />
              <h2 className="text-xl font-semibold text-slate-950">Email Agent Configuration</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Choose Gmail or Outlook, then import only email CVs that match the selected job.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { key: "gmail", label: "Gmail", badge: "In progress", disabled: true, description: "Google verification is in progress; keep using Outlook or direct upload for launch-ready intake." },
              { key: "outlook", label: "Outlook / Microsoft 365", description: "Use Microsoft Graph for company mailboxes." },
            ].map((provider) => (
              <button
                key={provider.key}
                type="button"
                disabled={provider.disabled}
                onClick={() => !provider.disabled && handleEmailProviderSelect(provider.key)}
                className={`rounded-lg border p-4 text-left transition ${
                  provider.disabled
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70"
                    : selectedEmailProvider === provider.key
                    ? "border-emerald-300 bg-emerald-50 hover:border-emerald-300"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-950">{provider.label}</p>
                  {provider.badge && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                      {provider.badge}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-500">{provider.description}</p>
              </button>
            ))}
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Agent goal</span>
            <textarea
              value={agentGoal}
              onChange={(event) => setAgentGoal(event.target.value)}
              rows={3}
              placeholder={`Import ${getEmailProviderName(selectedEmailProvider)} candidates for the selected job and prepare them for recruiter review.`}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>

          <button
            type="button"
            onClick={handleConnectEmail}
            disabled={!canImportCandidates || importing || !selectedImportJobId}
            className="inline-flex min-h-[40px] w-fit items-center justify-center gap-2 rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:opacity-50"
          >
            <Mail size={16} />
            {importing ? "Agent Running..." : `Run ${getEmailProviderName(selectedEmailProvider)} Agent`}
          </button>

          <div>
            <button
              type="button"
              onClick={() => setShowMailboxSettings((prev) => !prev)}
              className="text-sm font-semibold text-slate-600 transition hover:text-slate-950"
            >
              {showMailboxSettings ? `Hide ${getEmailProviderName(selectedEmailProvider)} settings` : `Advanced ${getEmailProviderName(selectedEmailProvider)} settings`}
            </button>
          </div>

          {showMailboxSettings && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm leading-6 text-emerald-800">
                Email import checks recent PDF attachments within the selected import window. Use Inbox only for the safest import,
                or choose a specific folder/label when applicants are stored outside the Inbox.
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(220px,280px)_minmax(220px,280px)_160px_140px]">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">{getEmailProviderName(selectedEmailProvider)} address</span>
                  <input
                    name="email_address"
                    value={mailboxSettings.email_address}
                    onChange={handleMailboxChange}
                    placeholder={selectedEmailProvider === "outlook" ? "recruiter@company.com" : "recruiter@gmail.com"}
                    disabled={!canImportCandidates}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Import source</span>
                  <select
                    name="mailbox_scope"
                    value={mailboxSettings.mailbox_scope || "inbox"}
                    onChange={handleMailboxChange}
                    disabled={!canImportCandidates}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                  >
                    <option value="inbox">Inbox only</option>
                    <option value="folder">Specific folder/label</option>
                    <option value="all">Inbox and folders</option>
                  </select>
                </label>

                {mailboxSettings.mailbox_scope === "folder" && (
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">Folder / label name</span>
                    <input
                      name="mailbox_folder_name"
                      value={mailboxSettings.mailbox_folder_name || ""}
                      onChange={handleMailboxChange}
                      placeholder={selectedEmailProvider === "outlook" ? "Backend Applicants" : "Backend Applicants"}
                      disabled={!canImportCandidates}
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                    />
                  </label>
                )}

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Import query / keyword</span>
                  <input
                    name="import_query"
                    value={mailboxSettings.import_query}
                    onChange={handleMailboxChange}
                    placeholder={selectedEmailProvider === "outlook" ? "PHP Developer" : "has:attachment filename:pdf"}
                    disabled={!canImportCandidates}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Import window</span>
                  <select
                    name="lookback_days"
                    value={mailboxSettings.lookback_days}
                    onChange={handleMailboxChange}
                    disabled={!canImportCandidates}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                  >
                    <option value="7">Last 7 days</option>
                    <option value="14">Last 14 days</option>
                    <option value="30">Last 30 days</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Max emails</span>
                  <input
                    name="max_results"
                    type="number"
                    min="1"
                    max="25"
                    value={mailboxSettings.max_results}
                    onChange={handleMailboxChange}
                    disabled={!canImportCandidates}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={handleSaveMailboxSettings}
                disabled={!canImportCandidates || savingMailbox}
                className="mt-4 inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                {savingMailbox ? "Saving..." : "Save Settings"}
              </button>
            </div>
          )}

          {agentReport && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-semibold text-slate-950">Email Import Result</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {agentReport.agent_summary}
                  </p>
                </div>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                  {agentReport.summary?.staged_count || 0} ready for review
                </span>
              </div>

              {lastImportJobId && (
                <button
                  type="button"
                  onClick={() => navigate(buildBulkScanUrl(lastImportJobId, newlyImportedIds))}
                  className="mt-4 inline-flex min-h-[40px] items-center justify-center rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900"
                >
                  Scan imported applicants
                </button>
              )}

              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-800">Recommended Next Steps</p>
                <ul className="mt-2 grid gap-2 md:grid-cols-3">
                  {(agentReport.recommendations || []).map((recommendation, index) => (
                    <li key={`${recommendation}-${index}`} className="rounded-md border border-slate-200 bg-white p-3 text-sm leading-5 text-slate-600">
                      {recommendation}
                    </li>
                  ))}
                </ul>
              </div>

              {(agentReport.summary?.skipped_files || []).length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-950">Skipped / failed files</p>
                  <div className="mt-2 grid gap-2">
                    {(agentReport.summary?.skipped_files || []).map((item, index) => (
                      <div key={`${item.filename}-${index}`} className="rounded-md border border-amber-100 bg-white p-3 text-xs leading-5 text-amber-900">
                        <p className="font-semibold">{item.filename || "Unknown file"}</p>
                        <p className="mt-1 break-words">{item.reason || "No reason returned."}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(agentReport.plan || []).length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowImportPlanDetails((prev) => !prev)}
                    className="text-xs font-semibold text-slate-500 transition hover:text-slate-900"
                  >
                    {showImportPlanDetails ? "Hide technical import details" : "View technical import details"}
                  </button>

                  {showImportPlanDetails && (
                    <ol className="mt-3 grid gap-2 md:grid-cols-2">
                      {(agentReport.plan || []).map((step, index) => (
                        <li key={`${step.step}-${index}`} className="rounded-md border border-slate-200 bg-white p-3">
                          <p className="text-sm font-semibold text-slate-900">{step.step}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{step.detail}</p>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}

          {(pendingEmailImports.length > 0 || alreadyRejectedEmailImports.length > 0) && (
            <div ref={pendingEmailReviewRef} className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 scroll-mt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-semibold text-emerald-950">Approve Email Candidates</h3>
                  <p className="mt-1 text-sm leading-6 text-emerald-800">
                    These CVs matched the selected job and are waiting for your approval. Only approved candidates will be saved into the candidate library.
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedStagingIds(pendingEmailImports.map((item) => item.staging_id))}
                    disabled={pendingEmailImports.length === 0 || savingApprovedImports}
                    className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold text-emerald-800 disabled:opacity-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectStagedImports}
                    disabled={savingApprovedImports || selectedStagingIds.length === 0}
                    className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 disabled:opacity-50"
                  >
                    Reject selected
                  </button>
                  <button
                    type="button"
                    onClick={handleApproveStagedImports}
                    disabled={savingApprovedImports || selectedStagingIds.length === 0}
                    className="inline-flex min-h-[36px] items-center justify-center rounded-lg bg-emerald-700 px-3 text-xs font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-50"
                  >
                    {savingApprovedImports ? "Saving..." : `Approve ${selectedStagingIds.length}`}
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {pendingEmailImports.map((item) => (
                  <label
                    key={item.staging_id}
                    className="flex cursor-pointer gap-3 rounded-lg border border-emerald-200 bg-white p-3"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStagingIds.includes(item.staging_id)}
                      onChange={() => toggleStagedImportSelection(item.staging_id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <p className="truncate text-sm font-semibold text-slate-950">{item.filename}</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="w-fit rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                            {item.provider === "outlook" ? "Outlook" : "Gmail"}
                          </span>
                          {item.application_quality && (
                            <span className={`w-fit rounded-full border px-2 py-1 text-xs font-semibold ${getApplicationQualityClass(item.application_quality)}`}>
                              {item.application_quality.label}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 grid gap-1 rounded-lg border border-emerald-100 bg-emerald-50/70 p-3 text-xs leading-5 text-slate-600">
                        <p className="break-words">
                          <span className="font-semibold text-slate-800">From:</span> {item.source_email || "Unknown sender"}
                        </p>
                        <p className="break-words">
                          <span className="font-semibold text-slate-800">Subject:</span> {item.email_subject || "No subject"}
                        </p>
                        <p>
                          <span className="font-semibold text-slate-800">Date:</span> {formatEmailImportDate(item.email_received_at || item.email_date || item.created_at)}
                        </p>
                      </div>
                      {item.application_quality?.reasons?.length > 0 && (
                        <details className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <summary className="cursor-pointer list-none text-xs font-semibold text-slate-700">
                            Application quality · {item.application_quality.label}
                            <span className="ml-2 text-slate-400">Show details</span>
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
                            {item.application_quality.reasons.map((reason, index) => (
                              <li key={`${item.staging_id}-quality-${index}`} className="flex gap-2">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      <p className="mt-2 text-xs leading-5 text-emerald-800">{item.relevance_reason}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.detected_email && (
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                            {item.detected_email}
                          </span>
                        )}
                        {item.top_qualification && (
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                            {item.top_qualification}
                          </span>
                        )}
                        {(item.detected_skills || []).slice(0, 5).map((skill) => (
                          <span key={`${item.staging_id}-${skill}`} className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                ))}
                {pendingEmailImports.length === 0 && (
                  <div className="rounded-lg border border-dashed border-emerald-200 bg-white/70 p-4 text-sm text-emerald-800">
                    No new email candidates are waiting for approval.
                  </div>
                )}
                {alreadyRejectedEmailImports.length > 0 && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h4 className="font-semibold text-rose-950">Already rejected for this job</h4>
                        <p className="mt-1 text-sm leading-6 text-rose-800">
                          These CVs were found again, but they stay out of approval because you rejected them earlier for this job.
                        </p>
                      </div>
                      <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-rose-800">
                        {alreadyRejectedEmailImports.length} found again
                      </span>
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {alreadyRejectedEmailImports.map((item) => (
                        <div key={`already-rejected-email-${item.staging_id}-${item.filename}`} className="rounded-lg border border-rose-100 bg-white p-3 text-sm">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <p className="truncate font-semibold text-slate-950">{item.filename}</p>
                            <span className="w-fit rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                              Not in approval queue
                            </span>
                          </div>
                          <div className="mt-2 grid gap-1 text-xs leading-5 text-slate-600">
                            <p className="break-words">
                              <span className="font-semibold text-slate-800">From:</span> {item.source_email || "Unknown sender"}
                            </p>
                            <p className="break-words">
                              <span className="font-semibold text-slate-800">Subject:</span> {item.email_subject || "No subject"}
                            </p>
                            <p>
                              <span className="font-semibold text-slate-800">Date:</span> {formatEmailImportDate(item.email_received_at || item.email_date || item.created_at)}
                            </p>
                          </div>
                          <p className="mt-2 text-xs leading-5 text-rose-700">{item.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      <section className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Candidates", value: stats.total, icon: Users },
          { label: "Shortlisted", value: stats.shortlisted, icon: Star },
          { label: "With Contact", value: stats.withContact, icon: Mail },
          { label: "Avg. Best Score", value: `${stats.averageBestScore}%`, icon: Search },
        ].map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-500">{item.label}</p>
                <Icon size={18} className="text-slate-400" />
              </div>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{item.value}</p>
            </div>
          );
        })}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Candidate Library</h2>
            <p className="mt-1 text-sm text-slate-500">
              Reusable candidate records built from scanned CVs, grouped by CV content.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search candidates, skills, jobs"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 sm:w-72"
            />

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            >
              <option value="all">All candidates</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="review">Review</option>
            </select>
          </div>
        </div>

        {newlyImportedCandidates.length > 0 && (
          <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                  Newly imported
                </div>
                <h3 className="mt-3 text-base font-semibold text-emerald-950">
                  {newlyImportedCandidates.length} candidate{newlyImportedCandidates.length === 1 ? "" : "s"} added
                </h3>
                <p className="mt-1 text-sm leading-6 text-emerald-800">
                  These candidates were added in the latest import, so you can review them before scrolling through the full library.
                </p>
              </div>

              {lastImportJobId && (
                <button
                  type="button"
                  onClick={() => navigate(buildBulkScanUrl(lastImportJobId, newlyImportedIds))}
                  disabled={isImportStillPreparing}
                  className="inline-flex min-h-[38px] shrink-0 items-center justify-center rounded-lg bg-emerald-700 px-4 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isImportStillPreparing ? "Preparing for scan" : "Scan newly imported"}
                </button>
              )}
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {newlyImportedCandidates.slice(0, 6).map((candidate) => (
                <div key={`new-${candidate.candidate_id}`} className="rounded-lg border border-emerald-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">{candidate.name}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{candidate.latest_filename}</p>
                      {candidate.top_qualification && (
                        <p className="mt-1 truncate text-xs font-semibold text-emerald-700">
                          {candidate.top_qualification}
                        </p>
                      )}
                      <p className="mt-1 text-xs font-medium text-emerald-700">
                        Imported {formatDate(candidate.imported_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                        isCandidateReady(candidate)
                          ? "bg-emerald-100 text-emerald-800"
                          : getEnrichmentStatus(candidate) === "failed"
                          ? "bg-rose-100 text-rose-700"
                          : "animate-pulse bg-amber-100 text-amber-700"
                      }`}>
                        {isCandidateReady(candidate)
                          ? "Ready for scan"
                          : getEnrichmentStatus(candidate) === "failed"
                          ? "Failed"
                          : "Preparing for scan"}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                        isCandidateSearchIndexed(candidate)
                          ? "bg-emerald-50 text-emerald-700"
                          : getSearchIndexStatus(candidate) === "failed"
                          ? "bg-rose-50 text-rose-700"
                          : "animate-pulse bg-sky-50 text-sky-700"
                      }`}>
                        {isCandidateSearchIndexed(candidate)
                          ? "Search indexed"
                          : getSearchIndexStatus(candidate) === "failed"
                          ? "Search index failed"
                          : "Search indexing"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {fetching ? (
          <p className="text-sm text-slate-500">Loading candidates...</p>
        ) : displayedCandidates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center">
            <Users className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-sm font-medium text-slate-700">No candidates found</p>
            <p className="mt-1 text-sm text-slate-500">Scan CVs first, then candidates will appear here.</p>
          </div>
        ) : (
          <>
          <div className="space-y-3 md:hidden">
            {displayedCandidates.map((candidate) => (
              <article
                key={candidate.candidate_id}
                className={`rounded-lg border p-4 shadow-sm ${
                  isNewlyImported(candidate)
                    ? "border-emerald-300 bg-emerald-50/70"
                    : candidate.source === "gmail" || candidate.source === "outlook"
                    ? "border-amber-200 bg-amber-50/50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-slate-950">{candidate.name}</h3>
                    <p className="mt-1 truncate text-xs text-slate-500">{candidate.latest_filename}</p>
                    {candidate.top_qualification && (
                      <p className="mt-1 truncate text-xs font-semibold text-emerald-700">
                        {candidate.top_qualification}
                      </p>
                    )}
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Imported {formatDate(candidate.imported_at)}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.best_score || 0)}`}>
                    {Math.round(candidate.best_score || 0)}%
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                    {candidate.scan_count} scan{candidate.scan_count === 1 ? "" : "s"}
                  </span>
                  {isNewlyImported(candidate) && (
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                      Newly imported
                    </span>
                  )}
                  {!isCandidateReady(candidate) && (
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      getEnrichmentStatus(candidate) === "failed"
                        ? "bg-rose-100 text-rose-700"
                        : "animate-pulse bg-amber-100 text-amber-700"
                    }`}>
                      {getEnrichmentStatus(candidate) === "failed" ? "Enrichment failed" : "Preparing for scan"}
                    </span>
                  )}
                  {!isCandidateSearchIndexed(candidate) && (
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      getSearchIndexStatus(candidate) === "failed"
                        ? "bg-rose-100 text-rose-700"
                        : "animate-pulse bg-sky-100 text-sky-700"
                    }`}>
                      {getSearchIndexStatus(candidate) === "failed" ? "Search index failed" : "Search indexing"}
                    </span>
                  )}
                  {(candidate.source === "gmail" || candidate.source === "outlook") && (
                    <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                      {candidate.source === "outlook" ? "Outlook import" : "Gmail import"}
                    </span>
                  )}
                  {candidate.application_quality && (
                    <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${getApplicationQualityClass(candidate.application_quality)}`}>
                      {candidate.application_quality.label}
                    </span>
                  )}
                  {candidate.is_shortlisted ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                      Shortlisted
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                      Review
                    </span>
                  )}
                </div>

                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Jobs</p>
                    <div className="flex flex-wrap gap-1">
                      {(candidate.jobs || []).slice(0, 3).map((job) => (
                        <button
                          key={`${candidate.candidate_id}-mobile-${job.job_id}`}
                          onClick={() => job.job_id && navigate(`/jobs/${job.job_id}`)}
                          className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600"
                        >
                          {job.job_name}
                        </button>
                      ))}
                      {(candidate.jobs || []).length === 0 && (
                        <span className="text-xs text-slate-400">No job linked</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {(candidate.skills || []).slice(0, 5).map((skill) => (
                        <span key={`${candidate.candidate_id}-mobile-${skill}`} className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                          {skill}
                        </span>
                      ))}
                      {(candidate.skills || []).length === 0 && (
                        <span className="text-xs text-slate-400">No skills detected</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-2">
                  {candidate.email ? (
                    <a
                      href={`mailto:${candidate.email}`}
                      className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"
                    >
                      <Mail size={14} />
                      Email
                    </a>
                  ) : null}

                  {candidate.phone ? (
                    <a
                      href={`tel:${candidate.phone}`}
                      className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"
                    >
                      <Phone size={14} />
                      {candidate.phone}
                    </a>
                  ) : null}

                  {candidate.can_delete && (
                    <button
                      type="button"
                      onClick={() => handleDeleteImportedCandidate(candidate)}
                      className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600"
                    >
                      <Trash2 size={14} />
                      Delete bad import
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-3 pr-4">Candidate</th>
                  <th className="py-3 pr-4">Imported</th>
                  <th className="py-3 pr-4">Best Score</th>
                  <th className="py-3 pr-4">Jobs</th>
                  <th className="py-3 pr-4">Skills</th>
                  <th className="py-3 pr-4">Contact</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedCandidates.map((candidate) => (
                  <tr
                    key={candidate.candidate_id}
                    className={`border-b align-top last:border-0 ${
                      isNewlyImported(candidate)
                        ? "border-emerald-100 bg-emerald-50/60"
                        : candidate.source === "gmail" || candidate.source === "outlook"
                        ? "border-amber-100 bg-amber-50/40"
                        : "border-slate-100"
                    }`}
                  >
                    <td className="max-w-[240px] py-4 pr-4">
                      <p className="truncate font-semibold text-slate-950">{candidate.name}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{candidate.latest_filename}</p>
                      {candidate.top_qualification && (
                        <p className="mt-1 truncate text-xs font-semibold text-emerald-700">
                          {candidate.top_qualification}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {candidate.scan_count} scan{candidate.scan_count === 1 ? "" : "s"}
                        </span>
                        {isNewlyImported(candidate) && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                            Newly imported
                          </span>
                        )}
                        {!isCandidateReady(candidate) && (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            getEnrichmentStatus(candidate) === "failed"
                              ? "bg-rose-100 text-rose-700"
                              : "animate-pulse bg-amber-100 text-amber-700"
                          }`}>
                            {getEnrichmentStatus(candidate) === "failed" ? "Enrichment failed" : "Preparing for scan"}
                          </span>
                        )}
                        {!isCandidateSearchIndexed(candidate) && (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            getSearchIndexStatus(candidate) === "failed"
                              ? "bg-rose-100 text-rose-700"
                              : "animate-pulse bg-sky-100 text-sky-700"
                          }`}>
                            {getSearchIndexStatus(candidate) === "failed" ? "Search index failed" : "Search indexing"}
                          </span>
                        )}
                        {(candidate.source === "gmail" || candidate.source === "outlook") && (
                          <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            {candidate.source === "outlook" ? "Outlook import" : "Gmail import"}
                          </span>
                        )}
                        {candidate.application_quality && (
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getApplicationQualityClass(candidate.application_quality)}`}>
                            {candidate.application_quality.label}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-4 pr-4 text-xs font-medium text-slate-500">
                      {formatDate(candidate.imported_at)}
                    </td>

                    <td className="py-4 pr-4">
                      <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.best_score || 0)}`}>
                        {Math.round(candidate.best_score || 0)}%
                      </span>
                    </td>

                    <td className="max-w-[260px] py-4 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {(candidate.jobs || []).slice(0, 3).map((job) => (
                          <button
                            key={`${candidate.candidate_id}-${job.job_id}`}
                            onClick={() => job.job_id && navigate(`/jobs/${job.job_id}`)}
                            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                          >
                            {job.job_name}
                          </button>
                        ))}
                        {(candidate.jobs || []).length === 0 && (
                          <span className="text-xs text-slate-400">No job linked</span>
                        )}
                      </div>
                    </td>

                    <td className="max-w-[300px] py-4 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {(candidate.skills || []).slice(0, 5).map((skill) => (
                          <span key={`${candidate.candidate_id}-${skill}`} className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                            {skill}
                          </span>
                        ))}
                        {(candidate.skills || []).length === 0 && (
                          <span className="text-xs text-slate-400">No skills detected</span>
                        )}
                      </div>
                    </td>

                    <td className="py-4 pr-4">
                      <div className="flex flex-col gap-2">
                        {candidate.email ? (
                          <a
                            href={`mailto:${candidate.email}`}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-950"
                          >
                            <Mail size={14} />
                            Email
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">No email</span>
                        )}

                        {candidate.phone ? (
                          <a
                            href={`tel:${candidate.phone}`}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-950"
                          >
                            <Phone size={14} />
                            {candidate.phone}
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">No phone</span>
                        )}
                      </div>
                    </td>

                    <td className="py-4 pr-4">
                      {!isCandidateReady(candidate) ? (
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          getEnrichmentStatus(candidate) === "failed"
                            ? "bg-rose-50 text-rose-700"
                            : "animate-pulse bg-amber-50 text-amber-700"
                        }`}>
                          {getEnrichmentStatus(candidate) === "failed" ? "Failed" : "Preparing for scan"}
                        </span>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <span className={`w-fit rounded-full px-2 py-1 text-xs font-semibold ${
                            candidate.is_shortlisted
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}>
                            {candidate.is_shortlisted ? "Shortlisted" : "Review"}
                          </span>
                          {!isCandidateSearchIndexed(candidate) && (
                            <span className={`w-fit rounded-full px-2 py-1 text-xs font-semibold ${
                              getSearchIndexStatus(candidate) === "failed"
                                ? "bg-rose-50 text-rose-700"
                                : "animate-pulse bg-sky-50 text-sky-700"
                            }`}>
                              {getSearchIndexStatus(candidate) === "failed" ? "Search index failed" : "Search indexing"}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="py-4 pr-4">
                      {candidate.can_delete ? (
                        <button
                          type="button"
                          onClick={() => handleDeleteImportedCandidate(candidate)}
                          className="inline-flex min-h-[34px] items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                          title="Delete bad email import"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              Showing {displayedCandidates.length} of {candidateTotal} candidate{candidateTotal === 1 ? "" : "s"} - page {candidatePage} of {candidateTotalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCandidatePage((page) => Math.max(page - 1, 1))}
                disabled={candidatePage <= 1 || fetching}
                className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCandidatePage((page) => Math.min(page + 1, candidateTotalPages))}
                disabled={candidatePage >= candidateTotalPages || fetching}
                className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
          </>
        )}
      </section>

    </>
  );

  if (loading || !user) return <p className="text-center mt-20">Loading...</p>;

  return (
    <DashboardLayout
      user={user}
      eyebrow="Candidate database"
      title="Candidates"
      subtitle="Centralized candidate profiles from scan history, with contact actions and job context"
      leftPanel={leftPanel}
      mainContent={mainContent}
    />
  );
}
